import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Check } from 'lucide-react';

export default function TeamMemberModal({
  isOpen,
  onClose,
  onSubmit,
  member = null,
  permissions = [],
  isLoading = false
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [showPermissionsDropdown, setShowPermissionsDropdown] = useState(false);
  const [errors, setErrors] = useState({});

  const modalContentRef = useRef(null);
  const permissionsContainerRef = useRef(null);
  const dropdownRef = useRef(null);

  const isEditMode = !!member;

  useEffect(() => {
    if (isOpen) {
      if (member) {
        setName(member.name || '');
        setEmail(member.email || '');
        setPassword('');
        const existingSlugs = (member.permissions || []).map(p => p.slug);
        setSelectedPermissions(existingSlugs);
      } else {
        setName('');
        setEmail('');
        setPassword('');
        setSelectedPermissions([]);
      }
      setErrors({});
      setShowPermissionsDropdown(false);
    }
  }, [isOpen, member]);

  useEffect(() => {
    if (showPermissionsDropdown && modalContentRef.current && permissionsContainerRef.current) {
      setTimeout(() => {
        const modalContent = modalContentRef.current;
        const permissionsContainer = permissionsContainerRef.current;
        const dropdown = dropdownRef.current;

        if (modalContent && permissionsContainer && dropdown) {
          const containerRect = permissionsContainer.getBoundingClientRect();
          const dropdownRect = dropdown.getBoundingClientRect();
          const modalRect = modalContent.getBoundingClientRect();

          const dropdownBottom = dropdownRect.bottom;
          const modalBottom = modalRect.bottom;

          if (dropdownBottom > modalBottom - 20) {
            const scrollAmount = dropdownBottom - modalBottom + 20;
            modalContent.scrollBy({
              top: scrollAmount,
              behavior: 'smooth'
            });
          }
        }
      }, 50);
    }
  }, [showPermissionsDropdown]);

  useEffect(() => {
    if (!showPermissionsDropdown) return;

    const handleClickOutside = (e) => {
      const dropdown = e.target.closest('.permissions-dropdown-container');
      if (!dropdown) {
        setShowPermissionsDropdown(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPermissionsDropdown]);

  const validate = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!isEditMode && !password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      name: name.trim(),
      email: email.trim(),
      permissions: selectedPermissions,
    };

    if (!isEditMode || password.trim()) {
      payload.password = password.trim();
    }

    onSubmit(payload);
  };

  const togglePermission = (slug) => {
    setSelectedPermissions(prev =>
      prev.includes(slug)
        ? prev.filter(p => p !== slug)
        : [...prev, slug]
    );
  };

  const getPermissionLabel = (slug) => {
    for (const group of permissions) {
      const found = (group.permissions || []).find(p => p.slug === slug);
      if (found) return found.label;
    }
    return slug;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        ref={modalContentRef}
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {isEditMode ? 'Edit Team Member' : 'Add Team Member'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
              disabled={isLoading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs ${errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                disabled={isLoading}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Email *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs ${errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Password {isEditMode ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs ${errors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                disabled={isLoading}
                placeholder={isEditMode ? 'Enter new password (optional)' : ''}
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Permissions */}
            <div ref={permissionsContainerRef} className="permissions-dropdown-container">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Permissions
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowPermissionsDropdown(!showPermissionsDropdown)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-xs font-medium flex items-center justify-between bg-white hover:bg-gray-50 text-left"
                  disabled={isLoading}
                >
                  <span className="text-gray-700">
                    {selectedPermissions.length > 0
                      ? `${selectedPermissions.length} permission${selectedPermissions.length !== 1 ? 's' : ''} selected`
                      : 'Select permissions'}
                  </span>
                  <X className={`w-4 h-4 text-gray-500 transition-transform ${showPermissionsDropdown ? 'rotate-45' : ''}`} />
                </button>

                {showPermissionsDropdown && (
                  <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-lg shadow-xl border border-gray-200 z-30 max-h-80 overflow-y-auto p-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {permissions.length === 0 ? (
                      <div className="px-3 py-2.5 text-xs text-gray-500">
                        No permissions available
                      </div>
                    ) : (
                      permissions.map((group, index) => (
                        <div key={group.module || index} className="mb-4 last:mb-0">
                          <h4 className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 rounded-md mb-1">
                            {group.module || 'Unknown Module'}
                          </h4>
                          <div className="space-y-0.5">
                            {(group.permissions || []).map((permission, pIndex) => {
                              const isSelected = selectedPermissions.includes(permission.slug);
                              return (
                                <button
                                  key={permission.slug || pIndex}
                                  type="button"
                                  onClick={() => togglePermission(permission.slug)}
                                  className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between text-xs transition-colors ${isSelected ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                >
                                  <span>{permission.label || permission.slug}</span>
                                  {isSelected && (
                                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Selected permissions chips */}
              {selectedPermissions.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedPermissions.map((slug) => (
                    <span
                      key={slug}
                      className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold flex items-center gap-1.5 border border-emerald-200"
                    >
                      {getPermissionLabel(slug)}
                      <button
                        type="button"
                        onClick={() => togglePermission(slug)}
                        className="hover:text-emerald-900 transition-colors"
                        disabled={isLoading}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-xs font-bold shadow-md hover:shadow-lg disabled:opacity-70 flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Update Member' : 'Create Member'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

